import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChatStore } from '../../stores/chatStore'
import type { Therapist, TherapistStatus } from '../../stores/chatStore'
import TherapistAvatar from './TherapistAvatar'
import TherapistSwitcher from './TherapistSwitcher'
import SessionDebugPanel from '../debug/SessionDebugPanel'
import { useDevMode } from '../../hooks/useDevMode'
import { wsManager } from '../../api/websocket'

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.1.0'

interface ChatHeaderProps {
  therapist?: Therapist | null
  isConnected: boolean
  therapistStatus?: TherapistStatus
  isOnboardingMode?: boolean
  consiliumActive?: boolean
  consiliumPhase?: string | null
  consiliumCurrentSpeaker?: string | null
}

// Female therapists (for gendered status messages)
const FEMALE_THERAPISTS = new Set(['wendy', 'una'])

// Status text for header — gendered where needed
// Most Russian 3rd-person verbs are gender-neutral, but idle status differs
const statusMessages: Record<Exclude<TherapistStatus, 'character'>, string> = {
  idle: '',
  typing: 'печатает...',
  recording_audio: 'записывает аудио...',
  recording_video: 'записывает видео...',
  listening_audio: 'слушает аудио...',
  watching_video: 'смотрит видео...',
  analyzing_photo: 'рассматривает фото...',
  generating_voice: 'записывает голосовое...',
}

// Technical statuses that take priority over character status
const technicalStatuses: TherapistStatus[] = [
  'typing',
  'recording_audio',
  'recording_video',
  'listening_audio',
  'watching_video',
  'analyzing_photo',
  'generating_voice',
]

// Consilium participants for header avatars
const CONSILIUM_MEMBERS = [
  { type: 'finch', name: 'Гарольд' },
  { type: 'wendy', name: 'Венди' },
  { type: 'artem', name: 'Артём' },
  { type: 'una', name: 'Уна' },
] as const

// Quick help content per therapist type
const THERAPIST_HELP: Record<string, { title: string; lines: string[] }> = {
  finch: {
    title: 'Гарольд Финч — Координатор',
    lines: [
      'Гарольд ведёт твой случай и знает общую картину.',
      'Он проводит онбординг — знакомство с клиникой и первичную оценку.',
      'Организует консилиумы — обсуждения командой, как в настоящей клинике.',
      'Выбирает подходящего терапевта для тебя и корректирует план.',
      'Методологии: мотивационное интервьюирование, психодинамика, интегративный подход.',
    ],
  },
  wendy: {
    title: 'Венди — Поддержка и глубина',
    lines: [
      'Тепло, принятие, работа с эмоциями и паттернами.',
      'Помогает с тревогой, выгоранием, самопознанием и подготовкой к важным решениям.',
    ],
  },
  artem: {
    title: 'Артём — Честность и движение',
    lines: [
      'Прямой разговор, вызов, не даёт застрять в комфорте.',
      'Помогает когда циклишься, избегаешь ответственности или врёшь себе.',
    ],
  },
  una: {
    title: 'Уна — Отношения и близость',
    lines: [
      'Расставания, привязанность, одиночество, страх близости.',
      'Помогает понять паттерны в отношениях и отпустить то, что держит.',
    ],
  },
}

export default function ChatHeader({ therapist, isConnected, therapistStatus = 'idle', isOnboardingMode = false, consiliumActive = false, consiliumPhase, consiliumCurrentSpeaker }: ChatHeaderProps) {
  const navigate = useNavigate()
  const [showSwitcher, setShowSwitcher] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const characterStatusText = useChatStore((s) => s.characterStatusText)
  const connectionFailed = useChatStore((s) => s.connectionFailed)
  const playbackRate = useChatStore((s) => s.playbackRate)
  const cyclePlaybackRate = useChatStore((s) => s.cyclePlaybackRate)
  const { isDebugEnabled } = useDevMode()

  const handleClearChat = () => {
    if (confirm('Очистить всю историю чата? Это действие нельзя отменить.')) {
      wsManager.clearChat()
      setShowMenu(false)
    }
  }

  // Determine status text
  const getStatusText = () => {
    if (!isConnected) return connectionFailed ? 'Соединение потеряно' : 'Подключение...'

    // Technical statuses have priority
    if (technicalStatuses.includes(therapistStatus)) {
      return statusMessages[therapistStatus as Exclude<TherapistStatus, 'character'>]
    }

    // Character status (story-like description)
    if (therapistStatus === 'character' && characterStatusText) {
      return characterStatusText
    }

    return FEMALE_THERAPISTS.has(therapist?.type || '') ? 'Готова помочь' : 'Готов помочь'
  }

  const isActive = therapistStatus !== 'idle'
  const isCharacterStatus = therapistStatus === 'character' && characterStatusText

  return (
    <>
      <header data-testid="chat-header" className="glass border-b border-neutral-200/50 sticky top-0 z-10 safe-area-pt">
        {/* Session Debug Panel (DEC-050) - only in dev mode */}
        {isDebugEnabled && (
          <div className="max-w-2xl mx-auto px-4 pt-2">
            <SessionDebugPanel />
          </div>
        )}

        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Consilium Header — all therapist avatars with active speaker highlighted */}
            {consiliumActive ? (
              <div className="flex items-center gap-3 -ml-2 pl-2 pr-3 py-1">
                <div className="flex items-center gap-1">
                  {CONSILIUM_MEMBERS.map((member) => {
                    const isCurrentSpeaker = consiliumCurrentSpeaker === member.type
                    const ringColor =
                      member.type === 'wendy' ? 'ring-wendy' :
                      member.type === 'artem' ? 'ring-artem' :
                      member.type === 'una' ? 'ring-una' :
                      'ring-finch'
                    return (
                      <div
                        key={member.type}
                        className={`rounded-full overflow-hidden ring-2 transition-all duration-300 ${ringColor} ${
                          isCurrentSpeaker
                            ? 'w-10 h-10 ring-offset-1 shadow-md'
                            : 'w-7 h-7 opacity-50'
                        }`}
                      >
                        <img
                          src={`/avatars/${member.type}.png`}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )
                  })}
                </div>
                <div className="text-left">
                  <h1 className="font-semibold text-neutral-800">Консилиум</h1>
                  <p className="text-xs text-neutral-500">
                    {consiliumPhase === 'completed'
                      ? 'Завершён'
                      : consiliumCurrentSpeaker
                        ? `${CONSILIUM_MEMBERS.find(m => m.type === consiliumCurrentSpeaker)?.name || ''} говорит...`
                        : 'Подготовка...'}
                  </p>
                </div>
              </div>
            ) : isOnboardingMode ? (
              <div className="flex items-center gap-3 -ml-2 pl-2 pr-3 py-1">
                <TherapistAvatar
                  therapist={therapist}
                  size="md"
                  showStatus={true}
                  isOnline={isConnected}
                />

                <div className="text-left">
                  <h1 className="font-semibold text-neutral-800">
                    {therapist?.name || 'Гарольд'}
                  </h1>
                  <p className={`text-xs ${connectionFailed ? 'text-red-500 font-medium' : isActive ? 'text-primary-600 font-medium' : 'text-neutral-500'} ${isCharacterStatus ? 'italic' : ''}`}>
                    {getStatusText()}
                  </p>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowSwitcher(true)}
                className="flex items-center gap-3 hover:bg-neutral-100/50 -ml-2 pl-2 pr-3 py-1 rounded-xl transition-colors"
              >
                <TherapistAvatar
                  therapist={therapist}
                  size="md"
                  showStatus={true}
                  isOnline={isConnected}
                />

                <div className="text-left">
                  <div className="flex items-center gap-1">
                    <h1 className="font-semibold text-neutral-800">
                      {therapist?.name || 'Терапевт'}
                    </h1>
                    {/* Dropdown indicator */}
                    <svg className="w-4 h-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <p className={`text-xs ${connectionFailed ? 'text-red-500 font-medium' : isActive ? 'text-primary-600 font-medium' : 'text-neutral-500'} ${isCharacterStatus ? 'italic' : ''}`}>
                    {getStatusText()}
                  </p>
                </div>
              </button>
            )}

            {/* Actions */}
            <div className="flex items-center gap-1">
              {/* Version badge — mobile only (StatusBar handles desktop) */}
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-changelog'))}
                className="sm:hidden text-[10px] font-mono text-neutral-400 px-1.5 py-0.5 rounded-full bg-neutral-100/80 hover:bg-neutral-200/80 transition-colors"
                title={`v${APP_VERSION}`}
              >
                🍎 {APP_VERSION}
              </button>

              {/* Quick help about current therapist */}
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="btn-ghost p-2.5 rounded-xl"
                aria-label="О терапевте"
                title="О терапевте"
              >
                <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>

              {/* Playback speed — global for this chat */}
              <button
                onClick={cyclePlaybackRate}
                className={`btn-ghost px-2 py-1.5 rounded-xl text-xs font-bold min-w-[44px] min-h-[44px] flex items-center justify-center ${
                  playbackRate !== 1 ? 'text-primary-600' : 'text-neutral-500'
                }`}
                aria-label={`Скорость воспроизведения ${playbackRate}x`}
                title="Скорость воспроизведения"
              >
                {playbackRate}x
              </button>

              {/* Consilium button — visible when not in onboarding and no active consilium */}
              {!isOnboardingMode && !consiliumActive && (
                <button
                  onClick={() => wsManager.startConsilium()}
                  className="btn-ghost p-2.5 rounded-xl"
                  aria-label="Запустить консилиум"
                  title="Консилиум терапевтов"
                >
                  <svg className="w-5 h-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </button>
              )}

              {/* Consilium active indicator */}
              {consiliumActive && consiliumPhase !== 'completed' && (
                <button
                  onClick={() => wsManager.dismissConsilium()}
                  className="btn-ghost p-2.5 rounded-xl"
                  aria-label="Завершить консилиум"
                  title="Завершить консилиум"
                >
                  <svg className="w-5 h-5 text-finch animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </button>
              )}

              {/* Menu button */}
              <div className="relative">
                <button
                  data-testid="header-menu-btn"
                  onClick={() => setShowMenu(!showMenu)}
                  className="btn-ghost p-2.5 rounded-xl"
                  aria-label="Меню"
                >
                  <svg className="w-5 h-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-neutral-200 py-1 z-30">
                      <button
                        onClick={() => {
                          navigate('/settings')
                          setShowMenu(false)
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-neutral-50 flex items-center gap-3"
                      >
                        <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>Настройки</span>
                      </button>

                      <button
                        onClick={() => {
                          navigate('/help')
                          setShowMenu(false)
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-neutral-50 flex items-center gap-3"
                      >
                        <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>О Janus Therapy</span>
                      </button>

                      <hr className="my-1 border-neutral-100" />

                      <button
                        data-testid="menu-clear-chat"
                        onClick={handleClearChat}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-3"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        <span>Очистить чат</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Therapist Switcher Modal */}
      {showSwitcher && (
        <TherapistSwitcher
          currentType={therapist?.type}
          onClose={() => setShowSwitcher(false)}
        />
      )}

      {/* Quick Help Popup */}
      {showHelp && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowHelp(false)} />
          <div className="fixed inset-x-4 top-20 z-50 max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-neutral-200 p-5">
              {(() => {
                const help = THERAPIST_HELP[therapist?.type || 'finch'] || THERAPIST_HELP.finch
                return (
                  <>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={`/avatars/${therapist?.type || 'finch'}.png`}
                          alt={help.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-neutral-800 pt-2">{help.title}</h3>
                    </div>
                    <div className="space-y-1.5">
                      {help.lines.map((line, i) => (
                        <p key={i} className="text-sm text-neutral-600 leading-relaxed">{line}</p>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => {
                          setShowHelp(false)
                          navigate('/help')
                        }}
                        className="flex-1 py-2 rounded-xl bg-primary-50 hover:bg-primary-100 text-sm font-medium text-primary-700 transition-colors"
                      >
                        Подробнее о Janus
                      </button>
                      <button
                        onClick={() => setShowHelp(false)}
                        className="px-4 py-2 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-sm text-neutral-600 transition-colors"
                      >
                        Закрыть
                      </button>
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </>
      )}

    </>
  )
}
